// REST-клиент: база из VITE_API_BASE, Bearer access, авто-refresh на 401 (single-flight).
// Разбор единого error-конверта (плановый {error,message,details}) и дефолтного Nest
// ({message,error,statusCode}; message может быть string|string[]).

const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api/v1').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Сетевой/offline-сбой (fetch бросил, не дошли до сервера). */
export class NetworkError extends Error {
  constructor(message = 'Нет соединения') {
    super(message);
    this.name = 'NetworkError';
  }
}

// Мост к auth-стору — регистрируется при инициализации, без циклической зависимости.
interface AuthBridge {
  getAccessToken: () => string | null;
  refresh: () => Promise<boolean>; // true — обновили, можно повторить запрос
  onAuthFailure: () => void; // refresh не удался — разлогинить
}
let bridge: AuthBridge | null = null;
export function registerAuthBridge(b: AuthBridge): void {
  bridge = b;
}

let refreshInflight: Promise<boolean> | null = null;
function refreshOnce(): Promise<boolean> {
  if (!bridge) return Promise.resolve(false);
  if (!refreshInflight) {
    refreshInflight = bridge.refresh().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** не подставлять Authorization (login/refresh/logout) */
  auth?: boolean;
  /** не пытаться рефрешить на 401 (чтобы не зациклить refresh) */
  retryOn401?: boolean;
  signal?: AbortSignal;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* пустое/не-JSON тело */
  }
  const b = body as
    | { error?: string; message?: string | string[]; details?: unknown; statusCode?: number }
    | null;
  const rawMsg = b?.message;
  const message = Array.isArray(rawMsg) ? rawMsg.join('; ') : (rawMsg ?? res.statusText);
  // код: для 409 бэк шлёт ConflictException → используем 'version_conflict' эвристически
  const code =
    res.status === 409
      ? 'version_conflict'
      : (b?.error ?? `http_${res.status}`).toString().toLowerCase().replace(/\s+/g, '_');
  return new ApiError(res.status, code, message || 'Ошибка запроса', b?.details ?? body);
}

async function rawFetch<T>(path: string, opts: RequestOptions): Promise<T> {
  const useAuth = opts.auth !== false;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (useAuth) {
    const token = bridge?.getAccessToken() ?? null;
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new NetworkError();
  }

  if (res.status === 401 && useAuth && opts.retryOn401 !== false) {
    const ok = await refreshOnce();
    if (ok) return rawFetch<T>(path, { ...opts, retryOn401: false });
    bridge?.onAuthFailure();
    throw await parseError(res);
  }

  if (!res.ok) throw await parseError(res);

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, opts: RequestOptions = {}) => rawFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    rawFetch<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    rawFetch<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    rawFetch<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts: RequestOptions = {}) =>
    rawFetch<T>(path, { ...opts, method: 'DELETE' }),
};
